import angular from "angular";
import addChangeItemDiscountDialogTemplate from "./AddChangeItemDiscountDialog.template.html";

export const addChangeDiscountDialogComponent = {
  template: addChangeItemDiscountDialogTemplate,
  controller: AddChangeItemDiscountDialogController,
  transclude: {
    discountAcctInfo: '?discountAcctInfo'
  }
}

/*******************************************************************************
 * Service: AddChangeItemDiscountDialog
 *
 * This panel is used to create and modify <b>ItemDiscounts</b>. The discount
 * can be entered as a percentage that should be removed from the base item
 * price or as a discounted amount.
 *****************************************************************************/
/* @ngInject */ export function AddChangeItemDiscountDialogService($uibModal, SERVER, AddChangeItemConfig, $templateCache) {
  /***************************************************************************
  * show */
  /**
  * Show the modal dialog for reviewing and managing Item Discounts. This
  * will be used to input new Discounts or change existing Discounts.
  *
  * @param   selectedDiscount  Discount object to be managed. Null=New
  * @param   accounts          List of available accounts to record the
  *                            discount difference against.
  * @param   itemPrice         Original price of item.
  * @param   addMode           Flag to indicate that the <b>ItemDiscount</b>
  *                            being managed will be a new object.
  * @param   excludeTypes      List of discount types to exclude
  *
  * @return  Promise Object    Resolves with new/updated discount object
  ***************************************************************************/
  this.show = function (
    selectedDiscount,
    accounts,
    itemPrice,
    addMode,
    excludeTypes
  ) {
    var t = $templateCache;
    return $uibModal.open({
      templateUrl: AddChangeItemConfig.templateIDs.AddDiscountDialog,
      size: 'md',
      backdrop: 'static',
      resolve: {
        accountList: function () {
          return accounts;
        },
        addMode: function () {
          return addMode;
        },
        discount: function () {
          return selectedDiscount ? angular.copy(selectedDiscount) : {};
        },
        itemPrice: function () {
          return itemPrice || 0;
        },
        excludeTypes: function () {
          return excludeTypes;
        }
      },
      controller: angular.noop
    }).result;
  };
}

/*****************************************************************************
 * Dialog Controller */
/**
 * accounts, discount, itemPrice, addMode are injected from show
 * at modal open.
 *****************************************************************************/
/* @ngInject */ function AddChangeItemDiscountDialogController(
  $scope,
  $element,
  $transclude,
  CompilerService,
  Tools,
  DiscountTypes
) {
  /*--------------------------------------------------*/
  /* Copy the dialog propeties in to this controller. */
  /*--------------------------------------------------*/
  var $ctrl = Object.assign(this, $scope.$parent.$resolve);
  $ctrl.close = $scope.$parent.$close;
  $ctrl.dismiss = $scope.$parent.$dismiss;
  $ctrl.amtPrice = $ctrl.addMode ? $ctrl.itemPrice : $ctrl.discount.price;
  $ctrl.validators = {};

  /*------------------------------------------------------------------*/
  /* Insert transcluded content and add reference to this controller. */
  /*------------------------------------------------------------------*/
  CompilerService.transcludeSlots($element, { $ctrl: this }, $transclude);

  /*------------------------------------------*/
  /* Filter the list of available types to    */
  /* exclude those provided in excludedTypes. */
  /*------------------------------------------*/
  $ctrl.types = DiscountTypes.filter(function (type) {
    return $ctrl.excludeTypes.indexOf(type.value) < 0;
  });

  /***************************************************************************
   * finish */
  /**
   * Finish button handler. If data is valid, resolve and close the modal
   * dialog.
   ***************************************************************************/
  $ctrl.finish = function () {
    if (!$ctrl.validators.validate()) return;

    /*--------------------------*/
    /* Set final discount.price */
    /*--------------------------*/
    $ctrl.discount.price = Tools.roundToPrecision(Number($ctrl.amtPrice), 2);
    $ctrl.close($ctrl.discount);
  };

  /***************************************************************************
   * getTitle */
  /**
   * Returns text to display for the modal title.
   *
   * @return  String  Display correct title for dialog.
   ***************************************************************************/
  $ctrl.getTitle = function () {
    return $ctrl.addMode ? 'Add New Item Discount' : 'Update Item Discount';
  };

  /***************************************************************************
   * selectedType */
  /**
   * Getter/Setter for discount type.
   *
   * @param  value  Selected type.
   ***************************************************************************/
  $ctrl.selectedType = function (value) {
    /*------------------------------------------------------*/
    /* Getter - Return corresponding type for discount.type */
    /*------------------------------------------------------*/
    if (!arguments.length)
      return $ctrl.types
        .filter(function (t) {
          return t.value == $ctrl.discount.type;
        })
        .shift();

    /*-------------------------------*/
    /* Setter - Update discount.type */
    /*-------------------------------*/
    $ctrl.discount.type = value ? value.value : 0;
  };

  /***************************************************************************
   * updateValues */
  /**
   * Updates amtPercent or amtPrice whenever there is a change in
   * the associated view fields. Update the percent or discount price based
   * on the amtPrice and the forValue parameter. If forValue is true, then
   * the discount price will be used to determine the percent, otherwise the
   * discount price will be calculated from the percent.
   *
   * @param  forValue  True: Indicates update from Discount Price input.
   ***************************************************************************/
  $ctrl.updateValues = function (forValue) {
    var amtPercent = Number($ctrl.amtPercent);
    var amtPrice = Number($ctrl.amtPrice);

    if (forValue)
      $ctrl.amtPercent = Tools.roundToPrecision(
        Number((1 - amtPrice / $ctrl.itemPrice) * 100),
        2
      );
    else
      $ctrl.amtPrice = Tools.roundToPrecision(
        Number((1 - amtPercent / 100) * $ctrl.itemPrice),
        2
      );
  };
}
